import { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import { translations, type Locale, type Translations } from "./translations";
import { supabase } from "@/integrations/supabase/client";

interface LanguageContextType {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: Translations;
  dateLocale: string;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

const STORAGE_KEY = "drum-language";
const GERMAN_COUNTRIES = new Set(["DE", "AT", "CH", "LI"]);

function getSyncLocale(): { locale: Locale; needsIpCheck: boolean } {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved === "en" || saved === "de") {
    return { locale: saved, needsIpCheck: false };
  }
  const browserLang = (navigator.language || "").slice(0, 2);
  if (browserLang === "de") {
    return { locale: "de", needsIpCheck: false };
  }
  // Browser reports non-German (or is empty) — IP check as tiebreaker
  return { locale: "en", needsIpCheck: true };
}

async function fetchCountryCode(): Promise<string> {
  try {
    const res = await fetch("/api/geo");
    if (!res.ok) return "";
    const data = await res.json();
    return (data.country || "").toUpperCase();
  } catch {
    return "";
  }
}

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [locale, setLocaleState] = useState<Locale>(() => getSyncLocale().locale);

  const setLocale = (l: Locale) => {
    localStorage.setItem(STORAGE_KEY, l);
    setLocaleState(l);
  };

  // IP-based detection: runs once on first visit when browser lang is not "de"
  useEffect(() => {
    const { needsIpCheck } = getSyncLocale();
    if (!needsIpCheck) return;

    let cancelled = false;
    fetchCountryCode().then((country) => {
      if (cancelled) return;
      if (GERMAN_COUNTRIES.has(country)) {
        setLocale("de");
      } else {
        // Persist "en" so IP check doesn't run again on next visit
        localStorage.setItem(STORAGE_KEY, "en");
      }
    });
    return () => { cancelled = true; };
  }, []);

  // Sync locale to localStorage, document lang, and Supabase user metadata
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, locale);
    document.documentElement.lang = locale;
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        supabase.auth.updateUser({ data: { language: locale } });
      }
    });
  }, [locale]);

  const value: LanguageContextType = {
    locale,
    setLocale,
    t: translations[locale],
    dateLocale: locale === "de" ? "de-DE" : "en-US",
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
};
